import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { Input, Button } from "antd-mobile";
import message from "antd/es/message";
import { genericError } from "./Dashboard.js";
import { useNavigate, useLocation } from "react-router";
import Layout from "./Layout";
import { useIntl, FormattedMessage } from "react-intl";
import * as Sentry from "@sentry/react";
import api from "./api";
import dayjs from "dayjs";

const { REACT_APP_API_URL: API_URL } = process.env;

dayjs().format();

export function wrongCredentials(intl) {
  message.error(intl.formatMessage({ id: "wrong-credentials" }));
}

export function maintenance(intl) {
  message.warning(intl.formatMessage({ id: "maintenance" }));
}

export default function Login(props) {
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm();
  const [isLogin, setIsLogin] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();

  async function onLogin({ username, password }) {
    Sentry.logger.debug("Login attempt", { username });

    await api(
      {
        method: "post",
        url: `${API_URL}/api/token/`,
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Credentials": "true",
        },
        data: { username, password },
      },
      []
    )
      .then((response) => {
        if (response && response.data.access) {
          if (response.data.disabled) {
            Sentry.logger.warn("Login blocked - maintenance mode", {
              username,
            });
            maintenance(intl);
            return;
          }

          localStorage.setItem("token", response.data.access);
          // set expiry to match backend token duration minus a minute to be safe
          const expiry = dayjs().add(23, "hour").add(59, "minute").format();
          localStorage.setItem("expiry", expiry);
          localStorage.setItem("role", response.data.role);

          Sentry.setUser({ username });
          Sentry.logger.info("Login successful", {
            username,
            role: response.data.role,
          });

          const redirectTo = location.state?.from?.pathname;
          if (redirectTo) {
            navigate(redirectTo);
          } else {
            navigate("/dashboard");
          }
        }
      })
      .catch((error) => {
        if (
          error.response &&
          error.response.status &&
          error.response.status === 401
        ) {
          Sentry.logger.warn("Login failed - invalid credentials", {
            username,
          });
          wrongCredentials(intl);
        } else {
          Sentry.captureException(error, {
            contexts: {
              custom: { message: "Login failed", username },
            },
          });
          Sentry.logger.error("Login failed", {
            username,
            error: error.message,
          });
          genericError(intl);
        }
      });
  }

  async function onReset({ username }) {
    Sentry.logger.debug("Password reset attempt", { username });

    await api(
      {
        method: "post",
        url: `${API_URL}/accounts/reset-password`,
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Credentials": "true",
        },
        data: { username },
      },
      []
    )
      .then(() => {
        Sentry.logger.info("Password reset email sent", { username });
        window.location.reload(false);
      })
      .catch((error) => {
        Sentry.captureException(error, {
          contexts: {
            custom: { message: "Password reset failed", username },
          },
        });
        Sentry.logger.error("Password reset failed", {
          username,
          error: error.message,
        });
        if (
          error.response &&
          error.response.status &&
          error.response.status !== 200
        ) {
          genericError();
        }
      });
  }

  return (
    <Layout isLoggedIn={false} renderLocaleSwitcher={true}>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          alignItems: "center",
          background: "white",
          margin: 10,
          padding: 10,
          borderRadius: 10,
          marginTop: 50,
        }}
      >
        <>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              padding: 30,
              border: "1px solid #eeeeee",
              borderRadius: 10,
            }}
          >
            <form onSubmit={handleSubmit(isLogin ? onLogin : onReset)}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {/* Isolated container for username - limits autofill DOM mutations */}
                <div key="username-field" style={{ display: "contents" }}>
                  <Controller
                    name="username"
                    control={control}
                    defaultValue=""
                    rules={{
                      required: true,
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        autoComplete="username"
                        style={{
                          background: "white",
                          margin: 10,
                          padding: 10,
                          border: "1px solid #eeeeee",
                          borderRadius: 10,
                        }}
                        placeholder={intl.formatMessage({ id: "username" })}
                      />
                    )}
                  />
                  {errors.username &&
                    intl.formatMessage({ id: "field-required" })}
                </div>
                {/* Isolated container for password - limits autofill DOM mutations */}
                {isLogin && (
                  <div key="password-field" style={{ display: "contents" }}>
                    <Controller
                      name="password"
                      control={control}
                      defaultValue=""
                      rules={{
                        required: true,
                      }}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="password"
                          autoComplete="current-password"
                          style={{
                            background: "white",
                            margin: 10,
                            padding: 10,
                            border: "1px solid #eeeeee",
                            borderRadius: 10,
                          }}
                          placeholder={intl.formatMessage({ id: "password" })}
                        />
                      )}
                    />
                    {errors.password &&
                      intl.formatMessage({ id: "field-required" })}
                  </div>
                )}
                <Button style={{ width: "100%", margin: 10 }} type="submit">
                  {isLogin
                    ? intl.formatMessage({ id: "log-in" })
                    : intl.formatMessage({ id: "reset-password" })}
                </Button>
              </div>
            </form>
          </div>
          <div style={{ marginTop: 20 }}>
            {isLogin ? (
              <Button onClick={() => setIsLogin(false)}>
                <FormattedMessage id="password-reset" />
              </Button>
            ) : (
              <Button onClick={() => setIsLogin(true)}>
                <FormattedMessage id="back-to-login" />
              </Button>
            )}
          </div>
        </>
      </div>
    </Layout>
  );
}
